// src/pages/hr/HREmployees.jsx
import React, { useState, useEffect } from "react";
import { HR_NAV } from "../../utils/navigation";
import Layout from "../../components/common/Layout";
import { collection, onSnapshot } from "firebase/firestore";
import { db } from "../../firebase/config";

const PANEL_LABEL = "HR Panel";

const ROLE_LABELS = { admin: "Admin", hr: "HR", lead: "Team Lead", employee: "Employee" };

export default function HREmployees() {
  const [employees, setEmployees] = useState([]);
  const [search, setSearch] = useState("");
  const [filterRole, setFilterRole] = useState("all");
  const [filterDept, setFilterDept] = useState("all");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, "users"), (s) => {
      setEmployees(s.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return unsub;
  }, []);

  const depts = [...new Set(employees.map((e) => e.department).filter(Boolean))];

  const filtered = employees.filter((e) => {
    const matchSearch =
      (e.displayName || "").toLowerCase().includes(search.toLowerCase()) ||
      (e.employeeId || "").toLowerCase().includes(search.toLowerCase()) ||
      (e.email || "").toLowerCase().includes(search.toLowerCase());
    const matchRole = filterRole === "all" || e.role === filterRole;
    const matchDept = filterDept === "all" || e.department === filterDept;
    return matchSearch && matchRole && matchDept;
  });

  function getInitials(name = "") {
    return name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();
  }

  const avatarColors = ["#4f46e5", "#0ea5e9", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899", "#1b4b31"];
  function avatarColor(name = "") {
    let h = 0;
    for (let c of name) h = c.charCodeAt(0) + h * 31;
    return avatarColors[Math.abs(h) % avatarColors.length];
  }

  return (
    <Layout navItems={HR_NAV} panelLabel={PANEL_LABEL}>
      <style>{`
        .emp-grid { display: grid; grid-template-columns: 2fr 1fr; gap: 1.5rem; }
        @media (max-width: 900px) { .emp-grid { grid-template-columns: 1fr; } }
        .emp-filters { display: flex; gap: 0.75rem; flex-wrap: wrap; margin-bottom: 1.25rem; }
        .emp-filters input, .emp-filters select { padding: 0.5rem 0.85rem; border: 1px solid var(--border, #d1d5db); border-radius: 8px; font-size: 0.84rem; background: var(--bg-card, #fff); color: var(--text-primary, #111); outline: none; }
        .emp-filters input:focus, .emp-filters select:focus { border-color: #1b4b31; box-shadow: 0 0 0 3px rgba(27,75,49,0.1); }
        .emp-table { width: 100%; border-collapse: collapse; font-size: 0.84rem; }
        .emp-table th { background: var(--bg-secondary, #f9fafb); text-align: left; padding: 0.65rem 0.9rem; font-size: 0.74rem; font-weight: 700; color: var(--text-muted, #6b7280); text-transform: uppercase; letter-spacing: 0.05em; border-bottom: 2px solid var(--border, #e5e7eb); }
        .emp-table td { padding: 0.7rem 0.9rem; border-bottom: 1px solid var(--border, #f3f4f6); vertical-align: middle; }
        .emp-table tr:hover td { background: var(--bg-secondary, #f9fafb); cursor: pointer; }
        .emp-avatar { width: 34px; height: 34px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 0.78rem; font-weight: 700; color: #fff; flex-shrink: 0; }
        .emp-role-pill { display: inline-block; padding: 0.18rem 0.6rem; border-radius: 99px; font-size: 0.7rem; font-weight: 700; }
        .emp-role--admin { background: #fef3c7; color: #92400e; }
        .emp-role--hr { background: #ede9fe; color: #5b21b6; }
        .emp-role--lead { background: #dbeafe; color: #1e40af; }
        .emp-role--employee { background: #d1fae5; color: #065f46; }
        .emp-profile-card { background: var(--bg-card, #fff); border: 1px solid var(--border, #e5e7eb); border-radius: 12px; padding: 1.5rem; box-shadow: 0 2px 10px rgba(0,0,0,0.08); }
        .emp-profile-header { text-align: center; padding-bottom: 1rem; border-bottom: 1px solid var(--border, #e5e7eb); margin-bottom: 1rem; }
        .emp-profile-avatar { width: 72px; height: 72px; border-radius: 50%; font-size: 1.5rem; font-weight: 700; color: #fff; display: flex; align-items: center; justify-content: center; margin: 0 auto 0.75rem; }
        .emp-profile-name { font-size: 1.05rem; font-weight: 700; color: var(--text-primary, #111); }
        .emp-profile-id { font-size: 0.78rem; color: var(--text-muted); margin-top: 3px; }
        .emp-profile-row { display: flex; justify-content: space-between; padding: 0.45rem 0; border-bottom: 1px solid var(--border, #f3f4f6); font-size: 0.82rem; }
        .emp-profile-row:last-child { border-bottom: none; }
        .emp-profile-key { color: var(--text-muted); }
        .emp-profile-val { font-weight: 600; color: var(--text-primary, #111); text-align: right; max-width: 55%; word-break: break-word; }
      `}</style>

      <div className="page-header">
        <div>
          <h1 className="page-title">Employee Directory</h1>
          <p className="page-subtitle">{employees.length} users in the system</p>
        </div>
      </div>

      <div className="emp-filters">
        <input
          type="text"
          placeholder="🔍 Search by name, ID, or email…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ flexGrow: 1, minWidth: 200 }}
        />
        <select value={filterRole} onChange={(e) => setFilterRole(e.target.value)}>
          <option value="all">All Roles</option>
          <option value="employee">Employee</option>
          <option value="lead">Team Lead</option>
          <option value="hr">HR</option>
          <option value="admin">Admin</option>
        </select>
        <select value={filterDept} onChange={(e) => setFilterDept(e.target.value)}>
          <option value="all">All Departments</option>
          {depts.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
      </div>

      <div className="emp-grid">
        <div>
          <table className="emp-table">
            <thead>
              <tr>
                <th>Employee</th>
                <th>ID</th>
                <th>Role</th>
                <th>Department</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={5} style={{ textAlign: "center", color: "var(--text-muted)", padding: "2rem" }}>No employees found</td></tr>
              ) : (
                filtered.map((e) => (
                  <tr key={e.id} onClick={() => setSelected(e)}>
                    <td>
                      <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
                        <div className="emp-avatar" style={{ background: avatarColor(e.displayName || "") }}>
                          {getInitials(e.displayName)}
                        </div>
                        <div>
                          <div style={{ fontWeight: 600 }}>{e.displayName || "—"}</div>
                          <div style={{ fontSize: "0.72rem", color: "var(--text-muted)" }}>{e.email}</div>
                        </div>
                      </div>
                    </td>
                    <td style={{ fontFamily: "monospace", fontSize: "0.8rem" }}>{e.employeeId || "—"}</td>
                    <td>
                      <span className={`emp-role-pill emp-role--${e.role}`}>
                        {ROLE_LABELS[e.role] || e.role}
                      </span>
                    </td>
                    <td>{e.department || "—"}</td>
                    <td>
                      <span style={{
                        display: "inline-block", width: 8, height: 8, borderRadius: "50%",
                        background: e.isActive !== false ? "#10b981" : "#ef4444", marginRight: 6,
                      }} />
                      {e.isActive !== false ? "Active" : "Inactive"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div>
          {!selected ? (
            <div className="emp-profile-card" style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 200, color: "var(--text-muted)", fontSize: "0.88rem" }}>
              👈 Click an employee to view their profile
            </div>
          ) : (
            <div className="emp-profile-card">
              <div className="emp-profile-header">
                <div className="emp-profile-avatar" style={{ background: avatarColor(selected.displayName || "") }}>
                  {getInitials(selected.displayName)}
                </div>
                <div className="emp-profile-name">{selected.displayName}</div>
                <div className="emp-profile-id">{selected.employeeId}</div>
                <div style={{ marginTop: "0.5rem" }}>
                  <span className={`emp-role-pill emp-role--${selected.role}`}>{ROLE_LABELS[selected.role] || selected.role}</span>
                </div>
              </div>
              {[
                ["Email", selected.email],
                ["Department", selected.department],
                ["Employment Type", selected.employmentType],
                ["Joining Date", selected.joiningDate],
                ["Phone", selected.phone],
                ["Gender", selected.gender],
                ["Status", selected.isActive !== false ? "Active ✅" : "Inactive ❌"],
              ].map(([k, v]) => (
                <div className="emp-profile-row" key={k}>
                  <span className="emp-profile-key">{k}</span>
                  <span className="emp-profile-val">{v || "—"}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
